/* eslint-disable max-lines */
import { formatFacilityWithState } from 'common/utils/facilityLabel';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  TextField as MuiTextField, // eslint-disable-line sort-imports
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { resolveLotAllocationsApi } from 'api/inventory';
import useRoles from 'common/hooks/useRoles';
import {
  getDispensableBudHelperText,
  getDispensableBudMinIso,
} from 'modules/StockManagement/utils/stockLineValidation';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { getClaimSplit, previewResolution } from '../utils/allocationClaimSplit';

const DISCARD_REASON_OPTIONS = [
  'Contaminated',
  'Damaged',
  'Expired',
  'Missing',
  'Packaging Defect',
  'Short Shipped',
  'Temperature Excursion',
  'Wrong Item',
];

/**
 * Why a resolution came back, in the reviewer's terms.
 *
 * Every one of these names something correctable on the spot, and all of them used to arrive as
 * "Failed to resolve allocation" — which describes the request failing when the request was fine,
 * and says nothing about what was actually refused. Any new core error code on this mutation
 * needs a key here, or the dialog renders the generic line again.
 */
const RESOLVE_ERROR_MESSAGES = {
  INVENTORY_TALLY_BUD_REQUIRED:
    'This lot has no expiration date, so restocking it would add stock that cannot be dispensed. ' +
    'Enter the date from the packaging, or discard the quantity with a reason instead.',
  INVENTORY_LOT_BUD_INVALID:
    'That expiration date is not a valid calendar date. Check it against the packaging.',
  INVENTORY_LOT_BUD_ALREADY_SET:
    'This lot was given an expiration date by another user while this dialog was open. Close and ' +
    'reopen it to see the current lot details.',
  INVENTORY_TALLY_LOT_MERGE_REQUIRED:
    'This lot already exists with that expiration date at this facility. The two records refer ' +
    'to the same physical lot and must be merged in Inventory before this allocation can be ' +
    'resolved.',
  INVENTORY_UNVERIFIED_OVER_RESOLVE:
    `This lot no longer carries that much ${SYSTEM_DEFICIT.toLowerCase()} quantity — another user may have resolved ` +
    'it. Reopen the allocation to see the current amounts.',
  ITEM_LOCKED_FOR_PHYSICAL_COUNT:
    'This product is locked for a physical count. Finish or cancel the count, then resolve this ' +
    'allocation.',
};

const getResolveErrorMessage = (err) => {
  const code = err?.response?.errors?.[0]?.extensions?.code;
  return RESOLVE_ERROR_MESSAGES[code] ?? 'Failed to resolve allocation. Please try again.';
};

let reasonIdSeq = 0;
const newReasonEntry = () => {
  reasonIdSeq += 1;
  return { id: reasonIdSeq, qty: '', reasons: [] };
};

const isDirtyEntry = (entry) => String(entry.qty).trim() !== '' || entry.reasons.length > 0;

const SectionHeader = ({ label, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
    <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <Tooltip title={tooltip} arrow placement='right'>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
    </Tooltip>
  </Box>
);

SectionHeader.propTypes = {
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
};

const ResolveAllocationDialog = ({ open, row, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const hasAccess = useRoles();
  const [restockQty, setRestockQty] = useState('');
  const [restockBud, setRestockBud] = useState('');
  const [discardQty, setDiscardQty] = useState('');
  const [discardReasons, setDiscardReasons] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [howItWorksAnchor, setHowItWorksAnchor] = useState(null);

  const restockNum = Number(restockQty) || 0;
  const discardNum = Number(discardQty) || 0;
  const totalQty = row?.quantity ?? 0;
  // POM-6152 — the total still resolves in full; this only says how it splits, so a
  // part-confirmed row no longer reads as "20 units of deficit" when 2 are confirmed stock.
  const { openClaim, confirmed } = getClaimSplit(row);
  const isStraddle = Boolean(row?.isUnverified) && confirmed > 0;
  const preview = previewResolution({ openClaim, restockQty: restockNum, discardQty: discardNum });
  const deficitLabel = SYSTEM_DEFICIT.toLowerCase();

  const atLeastOneProvided = restockNum > 0 || discardNum > 0;
  const restockExceedsTotal = restockNum > totalQty;
  const discardExceedsTotal = discardNum > totalQty;
  const sumExceedsTotal = restockNum + discardNum > totalQty;
  const hasQtyError = restockExceedsTotal || discardExceedsTotal || sumExceedsTotal;

  // A lot recorded from a fulfilment scan can have no expiration date — one cannot be read off a
  // barcode. Restocking such a claim MINTS verified stock, and stock with no BUD is filtered out
  // of every dispense query, so the units would land on the shelf unusable while inflating
  // on-hand. Core refuses that restock (INVENTORY_TALLY_BUD_REQUIRED); the field is here so the
  // reviewer, who is holding the product, can supply the date instead of being turned away.
  //
  // Only the unverified crossing is held to it. An ordinary reservation return is stock that was
  // already received and already dispensable, so core lets it through — but the date is still
  // WORTH having on an undated lot, which is why the field is offered either way and required
  // only where it is enforced.
  //
  // Whether the date is DEMANDED is a property of the row, not of what has been typed yet: the
  // field has to read "Required" before a quantity is entered, or the reviewer fills the form and
  // only then discovers the rule. What the quantity decides is whether the rule BITES — a pure
  // discard on the same undated lot is unaffected by it.
  const lotNeedsBud = !row?.bud;
  const budRequired = lotNeedsBud && Boolean(row?.isUnverified);
  // Floored at tomorrow, the same rule the fill dialog applies and for the same reason:
  // restocking to a date already reached puts back stock that cannot be dispensed. A genuinely
  // short-dated lot has an exit — discard it with a reason.
  const budTooEarly = getDispensableBudHelperText(restockBud);
  const budMissing = budRequired && restockNum > 0 && !restockBud;
  const budInvalid = budMissing || Boolean(restockBud && budTooEarly);

  const dirtyReasons = discardReasons.filter(isDirtyEntry);
  const reasonQtySum = dirtyReasons.reduce((s, r) => s + (Number(r.qty) || 0), 0);
  const reasonsBalanced = discardNum === 0 || reasonQtySum === discardNum;
  const eachReasonValid = dirtyReasons.every((r) => Number(r.qty) >= 1 && r.reasons.length > 0);
  const discardReasonsValid =
    discardNum === 0 || (dirtyReasons.length > 0 && reasonsBalanced && eachReasonValid);

  const isValid = atLeastOneProvided && !hasQtyError && discardReasonsValid && !budInvalid;

  const handleClose = () => {
    setRestockQty('');
    setRestockBud('');
    setDiscardQty('');
    setDiscardReasons([]);
    setShowErrors(false);
    onClose();
  };

  const handleAddReason = () => setDiscardReasons((prev) => [...prev, newReasonEntry()]);

  const handleRemoveReason = (idx) => setDiscardReasons((prev) => prev.filter((_, i) => i !== idx));

  const handleReasonQtyChange = (idx, value) =>
    setDiscardReasons((prev) => prev.map((r, i) => (i === idx ? { ...r, qty: value } : r)));

  const handleReasonTagsChange = (idx, newTags) =>
    setDiscardReasons((prev) => prev.map((r, i) => (i === idx ? { ...r, reasons: newTags } : r)));

  const handleSubmit = async () => {
    setShowErrors(true);
    if (!isValid) return;

    const items = [];

    if (restockNum > 0) {
      items.push({
        allocationId: row.allocationId,
        lotNumber: row.lotNumber,
        quantity: restockNum,
        status: 'ADD_BACK_TO_INVENTORY',
        // Omitted rather than sent empty when the lot is already dated: core ignores it in that
        // case, and an empty string would fail the Date scalar before it got there.
        ...(restockBud && { expirationDate: restockBud }),
      });
    }

    if (discardNum > 0) {
      items.push({
        allocationId: row.allocationId,
        lotNumber: row.lotNumber,
        quantity: discardNum,
        status: 'DISCARDED',
        discardReasons: dirtyReasons
          .filter((r) => r.qty > 0 && r.reasons.length > 0)
          .map((r) => ({ qty: Number(r.qty), reasons: r.reasons })),
      });
    }

    setSubmitting(true);
    try {
      await resolveLotAllocationsApi({ items });
      enqueueSnackbar('Allocation resolved successfully', { variant: 'success' });
      handleClose();
      onSuccess?.();
    } catch (err) {
      enqueueSnackbar(getResolveErrorMessage(err), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!row) return null;

  const combinedQtyError = sumExceedsTotal && !restockExceedsTotal && !discardExceedsTotal;
  const showDiscardReasons = discardNum > 0 && !combinedQtyError;

  const formattedBud = (() => {
    if (!row.bud) return '—';
    try {
      return format(new Date(row.bud), 'MMM d, yyyy');
    } catch {
      return row.bud;
    }
  })();

  // Mirrors the BUD formatter below: a value that will not parse is shown as-is rather
  // than rendered as 'Invalid Date'.
  const formatVerifiedAt = (value) => {
    try {
      return format(new Date(value), 'MMM d, yyyy');
    } catch {
      return String(value);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
        Manage Allocation
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        {/* POM-6084 — an unverified claim is not a returned reservation. Restocking it is
            the act of asserting that stock inventory never received does exist, on the
            strength of a scan nobody has confirmed, and it cannot be undone. The reviewer
            is told before they type a quantity, not after. */}
        {row.isUnverified && (
          <Alert severity='warning' sx={{ mt: 2 }}>
            <Typography variant='body2' sx={{ fontWeight: 700 }}>
              {SYSTEM_DEFICIT} claim — inventory has no receiving record for this lot
            </Typography>
            <Typography variant='body2'>
              This quantity was recorded as dispensed at fulfilment. Anything you restock becomes
              confirmed stock permanently and cannot be returned to the{' '}
              {SYSTEM_DEFICIT.toLowerCase()} side. Restock only the quantity you can account for;
              leave the rest, or discard it with a reason.
            </Typography>
            {/* The confirmed share is still part of the allocation and is resolved with it —
                the old wording ("cannot be approved again") read as if it were blocked. What
                is permanent is that it can never return to the deficit side. */}
            {isStraddle && (
              <Typography variant='body2' sx={{ mt: 1, fontWeight: 700 }}>
                {`${confirmed} of these ${totalQty} units ${confirmed === 1 ? 'was' : 'were'} already confirmed as received${
                  row.verifiedAt ? ` on ${formatVerifiedAt(row.verifiedAt)}` : ''
                } — ${confirmed === 1 ? 'it resolves' : 'they resolve'} as ordinary stock and can no longer return to the ${deficitLabel} side.`}
              </Typography>
            )}
            {!isStraddle && row.verifiedAt && (
              <Typography variant='body2' sx={{ mt: 1, fontWeight: 700 }}>
                {`Part of this claim was already confirmed on ${formatVerifiedAt(row.verifiedAt)}. The quantity below is what is still open.`}
              </Typography>
            )}
          </Alert>
        )}

        {/* Drug summary card */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'grey.50',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2,
            my: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              {row.drugName}
            </Typography>

            {row.strength && (
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Strength:
                </Box>{' '}
                {row.strength}
              </Typography>
            )}

            <Box sx={{ mb: 1 }}>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 0.25 }}>
                Facility:{' '}
                {formatFacilityWithState(
                  row.fulfillingPharmacy?.name,
                  row.fulfillingPharmacy?.state
                ) || '—'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                NDC:{' '}
                <Box component='span' sx={{ fontFamily: 'monospace' }}>
                  {row.ndc}
                </Box>
                {' · '}Lot #:{' '}
                <Box component='span' sx={{ fontFamily: 'monospace' }}>
                  {row.lotNumber}
                </Box>
                {row.manufacturer ? ` · Manufacturer: ${row.manufacturer}` : ''}
              </Typography>
            </Box>

            <Typography variant='body2' color='text.secondary'>
              <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                Expiry Date:
              </Box>{' '}
              {formattedBud}
              {' · '}
              <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                Current Stock:
              </Box>{' '}
              {row.availableQty != null ? `${row.availableQty.toLocaleString()} units` : '—'}
            </Typography>
          </Box>

          {/* Total qty stat */}
          <Box
            sx={{
              textAlign: 'center',
              minWidth: 120,
              flexShrink: 0,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              px: 2,
              py: 1.5,
              bgcolor: '#fff',
            }}
          >
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                display: 'block',
                lineHeight: 1.3,
                fontSize: '0.65rem',
              }}
            >
              Total Qty
              <br />
              to Resolve
            </Typography>
            <Typography variant='h4' sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>
              {row.quantity}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              units
            </Typography>
            {isStraddle && (
              <Box data-testid='resolve-claim-split' sx={{ mt: 1, textAlign: 'left' }}>
                <Typography variant='caption' sx={{ display: 'block', fontWeight: 600 }}>
                  {openClaim} {deficitLabel}
                </Typography>
                <Typography variant='caption' sx={{ display: 'block', fontWeight: 600 }}>
                  {confirmed} confirmed stock
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Combined qty validation */}
        {combinedQtyError && (
          <Alert severity='error' sx={{ mb: 2, py: 0 }}>
            Combined qty ({restockNum + discardNum}) exceeds available ({totalQty}). Reduce one of
            the fields.
          </Alert>
        )}

        {showErrors && !atLeastOneProvided && !hasQtyError && (
          <Alert severity='error' sx={{ mb: 2, py: 0 }}>
            At least one of Restock Qty or Discard Qty is required.
          </Alert>
        )}

        {/* Restock section */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e0e0e0',
            borderLeft: '4px solid #2e7d32',
            borderRadius: 1,
            p: 2,
            mb: 2,
          }}
        >
          <SectionHeader
            label='Restock'
            tooltip='Units returned to available inventory. These will be counted toward current stock.'
          />
          <MuiTextField
            label='Restock Qty'
            size='small'
            type='number'
            sx={{ width: 200 }}
            InputProps={{
              // eslint-disable-line react/jsx-no-duplicate-props
              inputProps: { min: 0 },
              endAdornment: (
                <InputAdornment position='end'>
                  <Typography variant='caption' color='text.secondary'>
                    units
                  </Typography>
                </InputAdornment>
              ),
            }}
            value={restockQty}
            onChange={(e) => setRestockQty(e.target.value)}
            error={restockExceedsTotal}
            helperText={restockExceedsTotal ? `Max ${totalQty}` : undefined}
          />

          {/* Shown only for a lot that has none. A date already on the lot is part of its
              identity, and correcting it is an inventory action with its own approval — not
              something to slip into a reversal. */}
          {lotNeedsBud && (
            <MuiTextField
              label='Expiration date (BUD)'
              size='small'
              type='date'
              required={budRequired}
              sx={{ width: 200, mt: 2, display: 'block' }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getDispensableBudMinIso(), 'data-testid': 'restock-bud' }}
              value={restockBud}
              onChange={(e) => setRestockBud(e.target.value)}
              error={Boolean(budTooEarly) || (showErrors && budMissing)}
              helperText={
                budTooEarly ||
                (budRequired
                  ? 'Required — read it off the packaging. Restocked stock cannot be dispensed without it.'
                  : 'Optional — supplying it makes this lot dispensable again.')
              }
            />
          )}

          {restockNum > 0 && !restockExceedsTotal && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography
                data-testid='restock-preview'
                variant='caption'
                sx={{ color: 'success.main', fontWeight: 500 }}
              >
                {restockNum} units will be returned to available stock
                {preview.restockFromClaim > 0 &&
                  ` — clears ${preview.restockFromClaim} ${deficitLabel}${
                    preview.restockFromStock > 0
                      ? `, ${preview.restockFromStock} already confirmed`
                      : ''
                  }`}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Discard section */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e0e0e0',
            borderLeft: '4px solid #e65100',
            borderRadius: 1,
            p: 2,
          }}
        >
          <SectionHeader
            label='Discard'
            tooltip='Units permanently removed from inventory. A reason is required for each quantity group discarded.'
          />
          <MuiTextField
            label='Discard Qty'
            size='small'
            type='number'
            sx={{ width: 200 }}
            InputProps={{
              // eslint-disable-line react/jsx-no-duplicate-props
              inputProps: { min: 0 },
              endAdornment: (
                <InputAdornment position='end'>
                  <Typography variant='caption' color='text.secondary'>
                    units
                  </Typography>
                </InputAdornment>
              ),
            }}
            value={discardQty}
            onChange={(e) => {
              const val = e.target.value;
              setDiscardQty(val);
              if ((Number(val) || 0) > 0 && discardReasons.length === 0) {
                setDiscardReasons([newReasonEntry()]);
              }
            }}
            error={discardExceedsTotal}
            helperText={discardExceedsTotal ? `Max ${totalQty}` : undefined}
          />

          {/* A deficit discard closes the claim and moves no stock; a confirmed-stock discard
              takes units off on-hand. Said out loud because the two look identical here. */}
          {row.isUnverified && discardNum > 0 && !hasQtyError && (
            <Typography
              data-testid='discard-preview'
              variant='caption'
              sx={{ display: 'block', mt: 1, color: 'warning.dark', fontWeight: 500 }}
            >
              {[
                preview.discardFromClaim > 0 &&
                  `${preview.discardFromClaim} ${deficitLabel} closed with no stock change`,
                preview.discardFromStock > 0 &&
                  `${preview.discardFromStock} confirmed units removed from on-hand`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}

          {/* Discard reasons — nested inside Discard card */}
          {showDiscardReasons && (
            <Box sx={{ mt: 2 }}>
              <Accordion
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '4px !important',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: '#fff8e1',
                    borderRadius: '3px',
                    minHeight: '40px !important',
                    '& .MuiAccordionSummary-content': { my: '6px', alignItems: 'center', gap: 1 },
                  }}
                >
                  <WarningAmberIcon fontSize='small' sx={{ color: 'warning.main' }} />
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    Discard Reasons
                  </Typography>
                  {dirtyReasons.length > 0 && (
                    <Typography
                      variant='caption'
                      sx={{
                        color: reasonsBalanced ? 'success.main' : 'warning.dark',
                        fontWeight: 600,
                      }}
                    >
                      {reasonsBalanced
                        ? `· ✓ ${reasonQtySum} of ${discardNum} accounted`
                        : `· ${reasonQtySum} of ${discardNum} — ${discardNum - reasonQtySum} remaining`}
                    </Typography>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  {reasonsBalanced && dirtyReasons.length > 0 ? (
                    <Chip
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                      label='Balanced'
                      color='success'
                      size='small'
                      sx={{ mr: 1, height: 22, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }}
                    />
                  ) : (
                    <Chip
                      icon={
                        <WarningAmberIcon
                          sx={{ fontSize: '14px !important', color: '#666 !important' }}
                        />
                      }
                      label='Incomplete'
                      size='small'
                      sx={{
                        mr: 1,
                        height: 22,
                        fontSize: 11,
                        '& .MuiChip-label': { px: 0.75 },
                        bgcolor: 'grey.200',
                        color: 'text.secondary',
                      }}
                    />
                  )}
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 2, pb: 1.5 }}>
                  {discardReasons.length === 0 && (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Add at least one reason for discarding.
                    </Typography>
                  )}

                  {discardReasons.map((entry, idx) => {
                    const otherQtySum = discardReasons.reduce(
                      (s, r, ri) => (ri !== idx ? s + (Number(r.qty) || 0) : s),
                      0
                    );
                    const maxQty = discardNum - otherQtySum;
                    const qtyNum = Number(entry.qty);
                    const qtyExceedsMax = String(entry.qty).trim() !== '' && qtyNum > maxQty;
                    const qtyInvalid = String(entry.qty).trim() !== '' && qtyNum < 1;
                    const entryDirty = isDirtyEntry(entry);

                    let qtyHelperText;
                    if (showErrors && entryDirty && !String(entry.qty).trim()) {
                      qtyHelperText = 'Required';
                    } else if (qtyInvalid) {
                      qtyHelperText = 'Min 1';
                    } else if (qtyExceedsMax) {
                      qtyHelperText = maxQty <= 0 ? 'Fully accounted for' : `Max ${maxQty}`;
                    }

                    return (
                      <Box key={entry.id} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <Box sx={{ width: 80, flexShrink: 0 }}>
                            <MuiTextField
                              label='Qty *'
                              size='small'
                              fullWidth
                              type='number'
                              inputProps={{ min: 1, style: { textAlign: 'right' } }}
                              value={entry.qty}
                              onChange={(e) => handleReasonQtyChange(idx, e.target.value)}
                              error={
                                (showErrors && entryDirty && !String(entry.qty).trim()) ||
                                qtyInvalid ||
                                qtyExceedsMax
                              }
                              helperText={qtyHelperText}
                            />
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Autocomplete
                              multiple
                              freeSolo
                              disableCloseOnSelect
                              size='small'
                              options={DISCARD_REASON_OPTIONS}
                              value={entry.reasons}
                              onChange={(_, newReasons) => handleReasonTagsChange(idx, newReasons)}
                              filterSelectedOptions
                              renderTags={(value, getTagProps) =>
                                value.map((option, i) => (
                                  <Chip
                                    key={option}
                                    label={option}
                                    size='small'
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...getTagProps({ index: i })}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <MuiTextField
                                  // eslint-disable-next-line react/jsx-props-no-spreading
                                  {...params}
                                  label='Reasons *'
                                  error={showErrors && entryDirty && entry.reasons.length === 0}
                                  helperText={
                                    showErrors && entryDirty && entry.reasons.length === 0
                                      ? 'Required'
                                      : undefined
                                  }
                                />
                              )}
                            />
                          </Box>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', pt: '4px', flexShrink: 0 }}
                          >
                            <IconButton
                              size='small'
                              color='error'
                              onClick={() => handleRemoveReason(idx)}
                            >
                              <DeleteOutlineIcon fontSize='small' />
                            </IconButton>
                          </Box>
                        </Box>
                        {idx < discardReasons.length - 1 && <Divider sx={{ mt: 2 }} />}
                      </Box>
                    );
                  })}

                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<AddIcon />}
                    onClick={handleAddReason}
                  >
                    Add Reason
                  </Button>

                  {showErrors && discardNum > 0 && dirtyReasons.length === 0 && (
                    <Typography variant='caption' color='error' sx={{ display: 'block', mt: 1 }}>
                      At least one discard reason is required.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          size='medium'
          variant='text'
          color='info'
          startIcon={<InfoOutlinedIcon />}
          sx={{ textTransform: 'none', mr: 'auto' }}
          onMouseEnter={(e) => setHowItWorksAnchor(e.currentTarget)}
          onMouseLeave={() => setHowItWorksAnchor(null)}
        >
          How it works
        </Button>
        <Popover
          open={Boolean(howItWorksAnchor)}
          anchorEl={howItWorksAnchor}
          onClose={() => setHowItWorksAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          disableRestoreFocus
          sx={{ pointerEvents: 'none' }}
          slotProps={{
            paper: {
              sx: {
                maxWidth: 420,
                borderLeft: '4px solid',
                borderColor: 'info.main',
                p: 2,
                pointerEvents: 'none',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <InfoOutlinedIcon fontSize='small' sx={{ color: 'info.main' }} />
            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
              How it works
            </Typography>
          </Box>
          <Typography variant='body2' color='text.secondary' component='ol' sx={{ m: 0, pl: 2.5 }}>
            <li style={{ marginBottom: 6 }}>
              Enter the qty to restock and/or discard. You do not need to account for the full
              amount.
            </li>
            <li style={{ marginBottom: 6 }}>
              Any remaining units will stay in the review queue for a future decision.
            </li>
            <li>
              If discarding, add at least one reason entry. Reason quantities must total the discard
              qty.
            </li>
          </Typography>
        </Popover>
        <Button variant='outlined' onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        {hasAccess.resolveAllocation && (
          <LoadingButton
            variant='contained'
            loading={submitting}
            disabled={!isValid}
            onClick={handleSubmit}
          >
            Confirm
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

ResolveAllocationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.shape({
    allocationId: PropTypes.string,
    availableQty: PropTypes.number,
    bud: PropTypes.string,
    drugName: PropTypes.string,
    manufacturer: PropTypes.string,
    fulfillingPharmacy: PropTypes.shape({ name: PropTypes.string, state: PropTypes.string }),
    lotNumber: PropTypes.string,
    ndc: PropTypes.string,
    quantity: PropTypes.number,
    strength: PropTypes.string,
    // POM-6084
    isUnverified: PropTypes.bool,
    verifiedAt: PropTypes.string,
    // POM-6152
    settledQty: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

ResolveAllocationDialog.defaultProps = {
  row: null,
  onSuccess: undefined,
};

export default ResolveAllocationDialog;
